import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { babyProfileService } from '../services/babyProfileService';
import { useLogger } from '../utils/logger';
import DOMPurify from 'dompurify';

const BabyProfileContext = createContext(null);

export const BabyProfileProvider = ({ children }) => {
  const { currentUser, isAuthenticated } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [planLimitError, setPlanLimitError] = useState(null);
  // Remove logger from dependency array by using it outside useEffect
  const loggerInstance = useLogger('BabyProfileContext');

  // Use useCallback to memoize the fetchProfiles function
  const fetchProfiles = useCallback(async () => {
    if (!isAuthenticated || !currentUser) {
      setProfiles([]);
      setLoading(false);
      return;
    }
    
    try {
      const idToken = currentUser.idToken;
      const fetchedProfiles = await babyProfileService.getBabyProfiles(idToken);
      setProfiles(fetchedProfiles);
      // Only log once when profiles are fetched, not on every render
      if (process.env.NODE_ENV === 'development') {
        console.log('[BabyProfileContext] Baby profiles fetched:', fetchedProfiles.length);
      }
    } catch (err) {
      loggerInstance.error('Error fetching baby profiles', { error: err.message });
      setError('Failed to fetch baby profiles. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [currentUser, isAuthenticated]); // Remove logger from dependencies
  
  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);
  
  const addBabyProfile = async (profileData) => {
    if (!isAuthenticated || !currentUser) {
      loggerInstance.error('Cannot add baby profile: User not authenticated');
      throw new Error('You must be logged in to add a baby profile');
    }
    
    setLoading(true);
    setPlanLimitError(null); // Reset plan limit error
    
    try {
      const idToken = currentUser.idToken;
      
      // Sanitize input data to prevent XSS attacks
      const sanitizedData = {
        ...profileData,
        name: DOMPurify.sanitize(profileData.name),
        birthDate: DOMPurify.sanitize(profileData.birthDate),
        gender: DOMPurify.sanitize(profileData.gender),
        notes: profileData.notes ? DOMPurify.sanitize(profileData.notes) : '',
        editCount: 0 // Initialize edit count to 0 for new profiles
      };
      
      const newProfile = await babyProfileService.createBabyProfile(idToken, sanitizedData);
      setProfiles(prevProfiles => [...prevProfiles, newProfile]);
      if (process.env.NODE_ENV === 'development') {
        console.log('[BabyProfileContext] Baby profile added:', newProfile.profileId);
      }
      return newProfile;
    } catch (err) {
      // Check if this is a plan limit error
      if (err.status === 403) {
        loggerInstance.warn('Plan limit reached when adding baby profile', { error: err });
        setPlanLimitError(err);
        throw err;
      }
      
      loggerInstance.error('Error adding baby profile', { error: err.message });
      setError('Failed to add baby profile. Please try again later.');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const updateBabyProfile = async (profileId, profileData) => {
    if (!isAuthenticated || !currentUser) {
      loggerInstance.error('Cannot update baby profile: User not authenticated');
      throw new Error('You must be logged in to update a baby profile');
    }
    
    setLoading(true);
    try {
      const idToken = currentUser.idToken;
      
      // Get current profile to check edit count
      const currentProfile = profiles.find(p => p.profileId === profileId) || 
                            await babyProfileService.getBabyProfile(idToken, profileId);
      
      // Check if edit limit has been reached
      const currentEditCount = currentProfile.editCount || 0;
      if (currentEditCount >= 3) {
        throw new Error('Edit limit reached: Profile can only be edited 3 times');
      }
      
      // Sanitize input data to prevent XSS attacks
      const sanitizedData = {
        ...profileData,
        name: DOMPurify.sanitize(profileData.name),
        birthDate: DOMPurify.sanitize(profileData.birthDate),
        gender: DOMPurify.sanitize(profileData.gender),
        notes: profileData.notes ? DOMPurify.sanitize(profileData.notes) : '',
        editCount: currentEditCount + 1 // Increment edit count
      };
      
      const updatedProfile = await babyProfileService.updateBabyProfile(idToken, profileId, sanitizedData);
      setProfiles(prevProfiles => 
        prevProfiles.map(profile => 
          profile.profileId === profileId ? updatedProfile : profile
        )
      );
      if (process.env.NODE_ENV === 'development') {
        console.log('[BabyProfileContext] Baby profile updated:', DOMPurify.sanitize(profileId));
      }
      return updatedProfile;
    } catch (err) {
      loggerInstance.error('Error updating baby profile', { error: err.message, profileId });
      setError('Failed to update baby profile. Please try again later.');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const deleteBabyProfile = async (profileId) => {
    if (!isAuthenticated || !currentUser) {
      loggerInstance.error('Cannot delete baby profile: User not authenticated');
      throw new Error('You must be logged in to delete a baby profile');
    }
    
    setLoading(true);
    try {
      const idToken = currentUser.idToken;
      await babyProfileService.deleteBabyProfile(idToken, profileId);
      setProfiles(prevProfiles => prevProfiles.filter(profile => profile.profileId !== profileId));
      if (process.env.NODE_ENV === 'development') {
        console.log('[BabyProfileContext] Baby profile deleted:', DOMPurify.sanitize(profileId));
      }
      return true;
    } catch (err) {
      loggerInstance.error('Error deleting baby profile', { error: err.message, profileId });
      setError('Failed to delete baby profile. Please try again later.');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const getProfile = async (profileId) => {
    if (!isAuthenticated || !currentUser) {
      loggerInstance.error('Cannot get baby profile: User not authenticated');
      throw new Error('You must be logged in to view a baby profile');
    }
    
    // First check if we already have the profile in state
    const existingProfile = profiles.find(p => p.profileId === profileId);
    if (existingProfile) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[BabyProfileContext] Baby profile found in state:', DOMPurify.sanitize(profileId));
      }
      return existingProfile;
    }
    
    try {
      const idToken = currentUser.idToken;
      const profile = await babyProfileService.getBabyProfile(idToken, profileId);
      if (process.env.NODE_ENV === 'development') {
        console.log('[BabyProfileContext] Baby profile retrieved:', DOMPurify.sanitize(profileId));
      }
      return profile;
    } catch (err) {
      loggerInstance.error('Error getting baby profile', { error: err.message, profileId });
      setError('Failed to get baby profile. Please try again later.');
      throw err;
    }
  };
  
  // Expose the fetchProfiles function as refreshProfiles for components to use
  const refreshProfiles = useCallback(() => {
    return fetchProfiles();
  }, [fetchProfiles]);
  
  // Function to check if user has reached the child limit (max 2 children)
  const hasReachedChildLimit = useCallback(() => {
    // Check if user has 2 or more children
    return profiles.length >= 2;
  }, [profiles]);
  
  return (
    <BabyProfileContext.Provider value={{
      profiles,
      loading,
      error,
      planLimitError,
      setPlanLimitError,
      addBabyProfile,
      updateBabyProfile,
      deleteBabyProfile,
      getProfile,
      refreshProfiles,
      hasReachedChildLimit
    }}>
      {children}
    </BabyProfileContext.Provider>
  );
};

export const useBabyProfiles = () => {
  const context = useContext(BabyProfileContext);
  if (!context) {
    throw new Error('useBabyProfiles must be used within a BabyProfileProvider');
  }
  return context;
};
